import React, { useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Input, Select, Space, theme } from "antd";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import Table from "../../../../Common/Table";

import styles from "./index.module.scss";

export interface PageVisitData {
  key: number;
  id: number;
  pageName: string;
  visitors: number;
  unique: number;
  bounceRate: string;
}

const BASE_PAGES: PageVisitData[] = [
  {
    key: 1,
    id: 1,
    pageName: "home",
    visitors: 4234,
    unique: 3980,
    bounceRate: "41%",
  },
  {
    key: 2,
    id: 2,
    pageName: "products",
    visitors: 4234,
    unique: 3980,
    bounceRate: "41%",
  },
  {
    key: 3,
    id: 3,
    pageName: "about-us",
    visitors: 4234,
    unique: 3980,
    bounceRate: "41%",
  },
  {
    key: 4,
    id: 4,
    pageName: "contact",
    visitors: 4234,
    unique: 3980,
    bounceRate: "41%",
  },
  {
    key: 5,
    id: 5,
    pageName: "blog",
    visitors: 4234,
    unique: 3980,
    bounceRate: "41%",
  },
  {
    key: 6,
    id: 6,
    pageName: "services",
    visitors: 4234,
    unique: 3980,
    bounceRate: "41%",
  },
  {
    key: 7,
    id: 7,
    pageName: "pricing",
    visitors: 4234,
    unique: 3980,
    bounceRate: "41%",
  },
  {
    key: 8,
    id: 8,
    pageName: "checkout",
    visitors: 3892,
    unique: 3411,
    bounceRate: "38%",
  },
  {
    key: 9,
    id: 9,
    pageName: "cart",
    visitors: 3510,
    unique: 3120,
    bounceRate: "44%",
  },
  {
    key: 10,
    id: 10,
    pageName: "login",
    visitors: 3288,
    unique: 2974,
    bounceRate: "52%",
  },
  {
    key: 11,
    id: 11,
    pageName: "sign-up",
    visitors: 2960,
    unique: 2705,
    bounceRate: "57%",
  },
  {
    key: 12,
    id: 12,
    pageName: "careers",
    visitors: 2417,
    unique: 2260,
    bounceRate: "63%",
  },
  {
    key: 13,
    id: 13,
    pageName: "support",
    visitors: 2104,
    unique: 1893,
    bounceRate: "48%",
  },
  {
    key: 14,
    id: 14,
    pageName: "terms",
    visitors: 1560,
    unique: 1477,
    bounceRate: "71%",
  },
  {
    key: 15,
    id: 15,
    pageName: "privacy",
    visitors: 1342,
    unique: 1288,
    bounceRate: "74%",
  },
];

// Twelve pages of rows at the default page size, so the pager has something to
// page through. The rows above are written out; the rest cycle them.
// TODO(design): placeholder data — a developer needs to connect the real
// endpoint. See DEVELOPER_TASKS.md.
const pagesData: PageVisitData[] = Array.from({ length: 120 }, (_, index) => {
  const base = BASE_PAGES[index % BASE_PAGES.length];
  const pass = Math.floor(index / BASE_PAGES.length);

  return {
    ...base,
    key: index + 1,
    id: index + 1,
    pageName: pass === 0 ? base.pageName : `${base.pageName}-${pass + 1}`,
    visitors: Math.max(120, base.visitors - pass * 180),
    unique: Math.max(90, base.unique - pass * 160),
  };
});

// TODO @Oleg add translation to select
const FILTER_OPTIONS = [
  { value: "all", label: "All pages" },
  { value: "top", label: "Top viewed" },
  { value: "low", label: "Low viewed" },
];

export default function PageVisits({ title }: { title: string }) {
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  // The rows are a local array, so the toolbar search filters them here.
  // Returning false from onSearch stops ProTable pushing a keyword into the
  // request params — there is no request behind this table to receive one.
  const [query, setQuery] = useState("");
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const visibleRows = query
    ? pagesData.filter((row) =>
        row.pageName.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : pagesData;

  const columns: ProColumns<PageVisitData>[] = [
    {
      title: t("pages.dashboard.analytics.pageName"),
      dataIndex: "pageName",
      key: "pageName",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <a style={{ color: token.colorPrimary, fontWeight: 500 }} href="#">
          {record.pageName}
        </a>
      ),
    },
    {
      title: t("pages.dashboard.analytics.visitors"),
      dataIndex: "visitors",
      key: "visitors",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => record.visitors.toLocaleString(),
    },
    {
      title: t("pages.dashboard.analytics.unique"),
      dataIndex: "unique",
      key: "unique",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => record.unique.toLocaleString(),
    },
    {
      title: t("pages.dashboard.analytics.bounceRate"),
      dataIndex: "bounceRate",
      key: "bounceRate",
      sorter: true,
      hideInSearch: true,
    },
  ];

  // const tableRequest = (
  //   {
  //     current,
  //     pageSize,
  //     ...args
  //   }: Record<string, string> & {
  //     pageSize?: number | undefined;
  //     current?: number | undefined;
  //     keyword?: string | undefined;
  //   },
  //   sorter: Record<string, SortOrder>,
  // ): Promise<Partial<RequestData<???>>> => {
  //   const newParams = queryFilterParams({
  //     page: current ? `${current}` : '1',
  //     pageSize: pageSize ? `${pageSize}` : '10',
  //     ...args,
  //     ...getSorterParams(sorter),
  //   });

  //   setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

  //   return ????.fetch({
  //     ...newParams,
  //     name: newParams?.name || undefined,
  //   }).then((response) => {
  //     if (response) {
  //       const { data, total } = response;

  //       return ({
  //         data: (data || []).map(((item) => ({
  //           ...item,
  //           key: item.id,
  //         }))),
  //         success: true,
  //         total,
  //       });
  //     }

  //     return ({ data: [], success: false, total: 0 });
  //   });
  // };

  return (
    <Table
      key={`${tableKey}-${i18n.language}`}
      resetKey={() => setTableKey(Date.now())}
      // No card of its own: this table already sits in one. Without this
      // ProTable wraps itself in a second ProCard, so the DOM held a card body
      // inside a card body, each with its own padding.
      cardProps={false}
      // The heading on the first line, the select and the search together on
      // the second. Both live in `filter` because that is the only slot on the
      // second line — the toolbar's own search, the one options.search draws,
      // always sits in the right of the first line beside the title.
      toolbar={{
        multipleLine: true,
        title,
        filter: (
          <Space>
            <Select
              defaultValue="all"
              className={styles.filter}
              options={FILTER_OPTIONS}
              onChange={(value) => console.log("Filter changed:", value)}
            />
            <Input.Search
              placeholder={t("placeholder.search")}
              allowClear
              onSearch={setQuery}
            />
          </Space>
        ),
      }}
      // request={tableRequest}
      // Compact rows: these tables are a card's worth of figures, not a screen
      // of their own — and for the same reason the pager keeps only its pages,
      // without the page-size select or the jump-to-page field.
      size="small"
      pagination={{ showSizeChanger: false, showQuickJumper: false }}
      dataSource={visibleRows}
      columns={columns}
      // Not `style: { display: "none" }` — that leaves the search card in the
      // flow with its own 16px bottom margin. `false` removes it outright.
      search={false}
      // A dashboard card, not a full table screen: no reload, density or column
      // settings here. The search stays — it is the one control the card uses.
      // The search moved to the second line, so the toolbar's own is off along
      // with the rest of the controls this card does not carry.
      options={{
        reload: false,
        density: false,
        setting: false,
        search: false,
      }}
      columnsState={{
        persistenceKey: "pro-table-users",
        persistenceType: "localStorage",
      }}
    />
  );
}
